import { create, tsx } from '@dojo/framework/core/vdom';
import { ConnectStrip } from '../../widgets/ConnectStrip';
import { Link } from '@dojo/framework/routing/Link';
import { Hero } from '../../widgets/Hero';
import { Summary } from '../../widgets/Summary';

import Head from '../../widgets/Head';
import ContentWrapper from '../../widgets/ContentWrapper';
import Section from '../../widgets/Section';
import Image from '../../widgets/Image';
import ListColumns from '../../widgets/ListColumns';
import WhySitePen from '../../widgets/WhySitePen';

const engineeringImg = require('../../assets/images/illustration/engineering.svg');
const platformMobileImg = require('../../assets/images/illustration/phone-graphs.svg');
const platformsImg = require('../../assets/images/illustration/app-dev.svg');
const responsiveImg = require('../../assets/images/illustration/responsive.svg');
const technologiesImg = require('../../assets/images/logo-illustration/js-technologies-color.svg');

const factory = create();

export const CrossPlatformDevelopment = factory(function CrossPlatformDevelopment() {
	return (
		<div>
			<Head
				title="Cross Platform Development Services | SitePen"
				description="Cross platform app development company building enterprise solutions to improve processes, streamline operations, and empower business success."
			/>

			<Hero background="level3Yellow">
				{{
					xlTitle: (
						<div>
							Cross Platform
							<br />
							Development
						</div>
					),
					blurb:
						'SitePen offers class-leading expertise in cross platform app development for the world’s most discerning clients.'
				}}
			</Hero>

			<Section type="leading">
				<ContentWrapper>
					<Summary reverse collapse="large" paddingBottom="md">
						{{
							title: 'Reach Users on All Platforms',
							blurb: (
								<virtual>
									<p>
										Cross platform application development is a software
										development methodology that is designed to enable the
										simplified deployment of a unified software architecture
										across various mobile operating systems such as iOS and
										Android.
									</p>
									<p>
										These operating systems typically rely on different
										codebases to power the applications that run on them. As
										such a mobile app built specifically on Android might be
										developed natively in Kotlin, while a similar app built for
										iOS might be developed in its native language, Swift. The
										challenge with application development across multiple
										disparate operating systems is that it requires specialized
										software teams with expertise in each software language.
										Since these languages are unrelated, efforts to build from
										one operating system to another must essentially, be
										duplicated. This ultimately results in increased software
										development time and cost.
									</p>
								</virtual>
							),
							image: <Image src={platformsImg} size="lg" />
						}}
					</Summary>
					<Summary collapse="large">
						{{
							blurb: [
								<p>
									The alternative is to approach this problem using cross platform
									development. The process involves creating a software
									application in a single codebase, and then using cross platform
									app development frameworks to deploy this singular codebase
									across multiple operating systems. An example of such a
									framework is React Native, which uses a native layer to
									interface with each operating system’s native architecture, with
									hooks into a common web language, in this case JavaScript.
								</p>,
								<p>
									This development approach is also related to cross platform
									desktop app development, whereby this unified software
									architecture is deployed across desktop apps across various
									desktop operating systems such as MacOS and Windows.
								</p>
							],
							image: <Image src={platformMobileImg} lazy size="lg" />
						}}
					</Summary>
				</ContentWrapper>
			</Section>
			<Section shaded>
				<ContentWrapper size="small">
					<Summary stack>
						{{
							image: <Image src={responsiveImg} lazy size="md" />,
							title: 'Cross Platform Application Development',
							blurb: (
								<virtual>
									<p>
										Why cross platform development? The advantage of leveraging
										cross platform app development and cross platform desktop
										development is that it reduces the time and cost to develop
										and maintain a mobile, web and/or a desktop application. By
										utilizing a single development language, and leveraging a
										cross platform app development framework’s native tie-ins,
										the need for separate teams with duplicated efforts is
										eliminated.
									</p>
									<p>
										While this approach solves the issue of developing on
										multiple platforms, a cross platform app development company
										must also consider the effect it has on the application’s
										User Interface. In order to properly account for the various
										operating systems and viewing formats, an understanding of
										cross platform UI development is also required. This
										discipline entails creating a modular design in which
										components such as menu navigation, feeds, views and other
										design elements can be reorganized within a user interface
										with relative ease as the need arises. For example, a single
										app that is designed to be used for desktop, web, and
										mobile, will need to account for the various screen sizes of
										each platform. A mobile app has considerably less screen
										real estate when operating on a smartphone, when compared to
										a tablet. A web browser on a laptop might have less screen
										real estate than a desktop application on a 27” desktop like
										the iMac. Successful implementation of cross platform UI
										development will enable a designer to create a user
										interface that can be seamlessly integrated across multiple
										platforms.
									</p>
								</virtual>
							)
						}}
					</Summary>
				</ContentWrapper>
			</Section>
			<Section>
				<ContentWrapper>
					<WhySitePen>
						{{
							blurb: (
								<virtual>
									<p>
										SitePen is a{' '}
										<Link to="about">
											leading cross platform app development
										</Link>{' '}
										company based in the USA. We have expertise in performing
										cross platform app development services and have been
										helping our customers develop software over the past 20
										years.
									</p>
									<p>
										We can provide our cross platform application development
										solutions to enterprise clients and small businesses, alike.
										We’ve executed high performing software solutions for
										clients like Fannie Mae, and Marriott International, and
										have developed award-winning products for countless small
										business and startups. As an experienced team we have a
										formal design, development and testing process and utilize
										best practices in project management and software
										development. We are experts in Agile Project Management,
										allowing us to work closely with our clients to ideate,
										iterate, and build in a seamless and collaborative process.
									</p>
									<p>
										SitePen’s mission statement is rooted in three core
										functional values. They are knowledge, respect and
										integrity. These values allow us to retain top tier talent
										in a highly competitive landscape while attracting
										best-in-class clients. We believe that deploying the best
										software starts from a strong set of core values.
									</p>
									<p>
										Finally, what sets us apart is that we focus our experience
										in four key knowledge areas – web engineering, UI/UX design,
										business analysis, and project management. Together with our
										values, we believe we offer a unique approach to cross
										platform app development and software services, in general.
									</p>
								</virtual>
							)
						}}
					</WhySitePen>
				</ContentWrapper>
			</Section>
			<Section shaded>
				<ContentWrapper size="small">
					<Summary stack>
						{{
							image: <Image src={technologiesImg} lazy size="lg" />,
							title: 'Technologies We Use',
							blurb: (
								<virtual>
									<p>
										SitePen builds adaptable, performant and highly-scalable
										applications using a suite of modern development frameworks.
										We have <Link to="expertise">core expertise</Link> in
										JavaScript and TypeScript and utilize these web frameworks
										and the extended JavaScript ecosystem to deliver software
										excellent to our clients. With SitePen as your partner, you
										can be confident that we will create well-designed and a
										sustainable mobile application the right way on the first
										go.
									</p>
									<p>
										JavaScript-based languages are ideal as they are faster to
										deploy, generally cost less to develop, and allow for
										complete cross platform app development across nearly every
										imaginable platform, ecosystem and operating system. We can
										use these technologies to develop native performing mobile
										applications, hybrid apps, or progressive web applications
										for mobile, web and desktop.
									</p>
									<p>
										Below are just a few examples of frameworks and languages we
										use:
									</p>
									<ListColumns
										columnCount={2}
										maxWidth="60%"
										style="disc"
										paddingLeft="sm"
										itemPaddingBottom="sm"
									>
										<span>React</span>
										<span>Vue</span>
										<span>Node</span>
										<span>TypeScript</span>
										<span>Angular</span>
										<span>Dojo</span>
									</ListColumns>
									<p>
										We believe these represent some of the best cross platform
										development frameworks. Our collection of expertise allows
										us to leverage these frameworks to build both front-end
										solutions as well as the backend infrastructure that powers
										them. We are thought leaders in cross platform application
										development, enterprise application and cloud platform
										development services, and can provide our technical
										expertise to you at any point in the product and software
										development lifecycles.
									</p>
								</virtual>
							)
						}}
					</Summary>
				</ContentWrapper>
			</Section>
			<Section>
				<ContentWrapper>
					<Summary collapse="large">
						{{
							title: 'Choosing the Right Cross Platform App Development Company',
							blurb: [
								<p>
									SitePen is no stranger to cross platform development for iOS,
									Android and Windows. We encourage you to reach out to us to
									learn more about our entire suite of cross platform app
									development services.
								</p>,
								<p>
									With the <Link to="expertise">best-in-class technologies</Link>{' '}
									powering your application, we also focus considerable effort in
									cross platform UI development, meaning we utilize our team of
									designers to sit down with your company and walk through the
									best approach in turning your idea, or business objective into a
									meaningful design.
								</p>,
								<p>
									We offer a quick turnaround on quotes, and provide some of the
									most competitive rates and prices in our market. While other
									companies utilize bulky, and dated software languages, SitePen
									operates entirely within a lean ecosystem of light client but
									highly robust frameworks.
								</p>
							],
							image: <Image src={engineeringImg} lazy size="lg" />
						}}
					</Summary>
				</ContentWrapper>
			</Section>
			<Section type="trailing" shaded>
				<ConnectStrip trailing />
			</Section>
		</div>
	);
});

export default CrossPlatformDevelopment;
